// src/Components/OrderConfirmationModal.tsx

import React from 'react';
import { Order } from '../../Redux/ActiveStore/orderManageApi';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Box,
  Typography,
  IconButton,
  Stack,
  Paper,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { formatPrice } from '../../Formatter/formatPrice';

interface OrderConfirmationModalProps {
  selectedCart: string;
  orders: Order[];
  carts: any[]; // Replace `any` with the actual type if available
  onClose: () => void;
  onAccept: (cartUniqueId: string) => void;
  onReject: (cartUniqueId: string) => void;
  onCancelOrder: (orderId: string) => void;
  onRestoreOrder: (orderId: string) => void;
}

const OrderConfirmationModal: React.FC<OrderConfirmationModalProps> = ({
  selectedCart,
  orders,
  carts,
  onClose,
  onAccept,
  onReject,
  onCancelOrder,
  onRestoreOrder,
}) => {
  const selectedCartData = carts.find((cart) => cart.cartUniqueId === selectedCart);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          minWidth: '390px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box component="div">
          <Typography component="div" variant="h6">受注内容</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        <List sx={{ p: 0 }}>
          {orders
            .filter((order) => order.cartUniqueId === selectedCart)
            .map((order) => (
              <ListItem
                key={order.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  py: 2,
                  px: 0,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: order.status === 'REJECTED' ? 'action.hover' : 'background.paper',
                }}
              >
                <Box sx={{ position: 'relative', width: 60, height: 60 }}>
                  {order.menuImageUrl ? (
                    <Box
                      component="img"
                      src={`${process.env.REACT_APP_CLOUDFRONT_URL}${order.menuImageUrl}`}
                      alt={order.menuName}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 1,
                      }}
                    />
                  ) : (
                    <Paper
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.200',
                        borderRadius: 1,
                      }}
                    >
                      <ImageNotSupportedIcon color="disabled" />
                    </Paper>
                  )}
                </Box>

                <Stack
                  direction="column"
                  spacing={1}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.2,
                    }}
                  >
                    {order.menuName}
                  </Typography>
                  {order.menuOption && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {order.menuOption.menuOptionName}
                      {order.menuOption.menuOptionPrice && (
                        order.menuOption.menuOptionPriceType === 'ADDITIONAL' ? 
                        ` (+${order.menuOption.menuOptionPrice}円)` :
                        ` (-${order.menuOption.menuOptionPrice}円)`
                      )}
                    </Typography>
                  )}

                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {formatPrice(
                        order.menuOption?.menuOptionPrice
                          ? order.menuOption.menuOptionPriceType === 'ADDITIONAL'
                            ? order.menuPrice + order.menuOption.menuOptionPrice
                            : order.menuPrice - order.menuOption.menuOptionPrice
                          : order.menuPrice
                      )}円
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {order.quantity}個
                    </Typography>
                    {order.status === 'REJECTED' ? (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => onRestoreOrder(order.id)}
                      >
                        受付中に戻す
                      </Button>
                    ) : order.status === 'WAITING' || order.status === 'CLERK_ORDER' ? (
                      // 店員注文（CLERK_ORDER）も単品キャンセル可能にする（ADR 0067）。
                      // BE の cancel はステータスガードなしの明細単位 UPDATE のため API 変更は不要。
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => onCancelOrder(order.id)}
                      >
                        キャンセル
                      </Button>
                    ) : (
                      <Box sx={{ width: 100 }} />
                    )}
                  </Stack>
                </Stack>
              </ListItem>
            ))}
        </List>
      </DialogContent>

      {selectedCartData?.status === 'WAITING' && (
        <>
          <Divider />
          <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between' }}>
            <Box sx={{ width: '49%' }}>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={() => onReject(selectedCart)}
              >
                全てキャンセル
              </Button>
            </Box>
            <Box sx={{ width: '49%' }}>
              <Button
                variant="contained"
                color="success"
                fullWidth
                onClick={() => onAccept(selectedCart)}
              >
                承諾
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default OrderConfirmationModal;